<template>
  <div class="lead-assign-history">
    <div class="d-flex flex-wrap justify-content-between align-items-center mb-1">
      <p class="text-muted small mb-0">
        Every bulk assign, transfer, and assign-to change for this lead.
      </p>
      <div>
        <b-button
          size="sm"
          variant="outline-primary"
          class="mr-50"
          :disabled="loading"
          @click="fetchHistory"
        >
          <feather-icon icon="RefreshCwIcon" size="14" />
        </b-button>
        <b-button
          size="sm"
          variant="outline-secondary"
          @click="openFullTracking"
        >
          View all tracking
        </b-button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-3">
      <b-spinner small label="Loading assignment history" />
    </div>

    <b-alert v-else-if="error" show variant="danger" class="mb-0">
      {{ error }}
    </b-alert>

    <b-alert v-else-if="!logs.length" show variant="light" class="mb-0">
      No assignment history recorded for this lead yet.
    </b-alert>

    <b-table
      v-else
      responsive
      striped
      hover
      small
      :items="logs"
      :fields="fields"
      class="mb-0"
    >
      <template #cell(source_label)="data">
        <b-badge :variant="sourceVariant(data.item.source)">
          {{ data.item.source_label || formatSource(data.item.source) }}
        </b-badge>
      </template>
      <template #cell(from_to)="data">
        <span class="small">
          {{ userLabel(data.item.transfer_from_user) }}
          <feather-icon icon="ArrowRightIcon" size="12" class="mx-25" />
          {{ userLabel(data.item.assignee) }}
        </span>
      </template>
      <template #cell(transfer_by_user)="data">
        {{ userLabel(data.item.transfer_by_user) }}
      </template>
      <template #cell(created_at)="data">
        {{ formatDate(data.value) }}
      </template>
      <template #cell(remarks)="data">
        <span class="small text-muted">{{ data.value || "—" }}</span>
      </template>
      <template #cell(batch_id)="data">
        <span
          v-if="data.value"
          class="small text-monospace"
          :title="data.value"
        >
          {{ shortBatch(data.value) }}
        </span>
        <span v-else class="text-muted">—</span>
      </template>
    </b-table>
  </div>
</template>

<script>
import axios from "@/components/axios";
import moment from "moment";
import { BAlert, BBadge, BButton, BSpinner, BTable } from "bootstrap-vue";

export default {
  name: "LeadAssignHistory",
  components: { BAlert, BBadge, BButton, BSpinner, BTable },
  props: {
    leadId: {
      type: [Number, String],
      required: true,
    },
    autoLoad: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      accessToken: localStorage.getItem("accessToken"),
      baseApi: process.env.VUE_APP_APIENDPOINT,
      loading: false,
      error: null,
      logs: [],
      fields: [
        { key: "created_at", label: "Date & time" },
        { key: "source_label", label: "Source" },
        { key: "from_to", label: "From → To" },
        { key: "transfer_by_user", label: "By" },
        { key: "remarks", label: "Remarks" },
        { key: "batch_id", label: "Batch" },
      ],
    };
  },
  watch: {
    leadId: {
      immediate: true,
      handler(id) {
        if (this.autoLoad && id) {
          this.fetchHistory();
        }
      },
    },
  },
  methods: {
    async fetchHistory() {
      if (!this.leadId) {
        return;
      }
      this.loading = true;
      this.error = null;
      try {
        const res = await axios({
          method: "GET",
          url: `${this.baseApi}/lead-assign-logs/lead/${this.leadId}`,
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
          },
        });
        this.logs = res.data?.data?.logs || [];
      } catch (e) {
        this.logs = [];
        this.error =
          e.response?.data?.message ||
          "Could not load assignment history. Ensure the API migration has been run.";
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    openFullTracking() {
      this.$router.push({
        path: "/crm/lead/assign-tracking",
        query: { lead_id: String(this.leadId) },
      });
    },
    userLabel(user) {
      if (!user || !user.id) {
        return "Unassigned";
      }
      return user.fullname || user.name || user.username || `#${user.id}`;
    },
    formatDate(value) {
      if (!value) {
        return "—";
      }
      return moment(value).format("DD MMM YYYY, hh:mm A");
    },
    formatSource(source) {
      const map = {
        bulk_assign: "Bulk Assign",
        transfer: "Transfer",
        form_edit: "Lead Edit",
        initial_assign: "Initial Assignment",
      };
      return map[source] || source || "Assignment";
    },
    sourceVariant(source) {
      const map = {
        bulk_assign: "primary",
        transfer: "warning",
        form_edit: "info",
        initial_assign: "success",
      };
      return map[source] || "secondary";
    },
    shortBatch(id) {
      if (!id || id.length < 12) {
        return id || "";
      }
      return `${id.slice(0, 8)}…`;
    },
  },
};
</script>

<style scoped>
.lead-assign-history {
  min-height: 120px;
}
</style>
